





















































































import { Component, Mixins, Watch } from 'vue-property-decorator'
import { Track } from '@/classes/track'
import { IColumnDef } from '@/interfaces/ag-grid'
import { findTracksWithRegEx, regexReplace, REGEX_KEY_PREFIX } from '@/helpers/features/smartfixes/regex-replace'
import { TrackFields, TrackFieldsLabels } from '@/constants'
import { LibraryMixin } from '@/mixins'
import { USAGE_STAT_FEATURES } from '@/system/usage-stats'
import { waitForMinimumDelay } from '@/helpers/date'
import { ILabelValue } from '@/interfaces/generic'
import { FILTER_RULES_FIELDS, FieldTypes } from '@/helpers/filter-rules'
import { TRANSFORMERS, ITransformerSettings } from '@/helpers/transformer'

interface IRegexTemplate {
  label: string
  explanation: string
  regex: string
  replace: string
  field: TrackFields
  flags: string[]
}

const REGEX_TEMPLATES: IRegexTemplate[] = [
  {
    label: 'Remove key prefix from title',
    explanation: '"10A - Track Title" and "10A Track Title" will be converted to "Track Title"',
    regex: REGEX_KEY_PREFIX,
    flags: ['i'],
    replace: '',
    field: TrackFields.Title
  },
  {
    label: 'Convert MP3 extension to WAV',
    explanation: 'The .mp3 extension in files will be changed to .wav',
    regex: `\\.mp3$`,
    flags: ['i'],
    replace: '.wav',
    field: TrackFields.Location
  },
  {
    label: 'Convert FLAC extension to MP3',
    explanation: 'The .flac extension in files will be changed to .mp3',
    regex: `\\.flac$`,
    flags: ['i'],
    replace: '.mp3',
    field: TrackFields.Location
  },
  {
    label: 'Remove "(Original Mix)" from title',
    explanation: 'Removes "(Original Mix)" from track titles. Change the values as needed.',
    regex: `\\s+(-?\\s*\\(?(original\\ +mix)\\)?)$`,
    flags: ['i'],
    replace: '',
    field: TrackFields.Title
  }
]

@Component({
  components: {
    ReorderTable: require('@/components/ReorderTable').default,
    SmartFix: require('../SmartFix').default
  }
})
export default class RegexReplace extends Mixins(LibraryMixin) {
  scanned: boolean = false
  loading: boolean = false
  tracks: Track[] = []
  selectedTracks: Track[] = []
  errorMessage: string = ''

  regexString: string = ''
  replaceString: string = ''
  field: TrackFields = TrackFields.Title
  flagInsensitive: boolean = false
  flagGlobal: boolean = false

  selectedTemplate: IRegexTemplate | null = null

  REGEX_TEMPLATES = REGEX_TEMPLATES
  fields: ILabelValue[] = Object.keys(FILTER_RULES_FIELDS).filter(k => FILTER_RULES_FIELDS[k].type === FieldTypes.String).map(k => {
    return {
      value: k,
      label: TrackFieldsLabels[k]
    } as ILabelValue
  })

  columnDefs: IColumnDef[]

  get scanDisabled(): boolean {
    return !this.regexString || !this.field
  }

  get regex(): RegExp {
    let flags = ''

    if (this.flagInsensitive) flags += 'i'
    if (this.flagGlobal) flags += 'g'

    const regex = new RegExp(this.regexString, flags)

    return regex
  }

  get disabled(): boolean {
    return this.selectedTracks.length === 0
  }

  regexReplace(track: Track) {
    return regexReplace(track, [this.field, this.regex, this.replaceString])
  }

  async scan() {
    if (this.loading || this.scanned || this.scanDisabled) return

    this.loading = true
    this.errorMessage = ''

    const startTime = new Date()

    try {
      this.tracks = findTracksWithRegEx(this.library.tracks, this.regex, this.field)

      await waitForMinimumDelay(startTime, 500)

      this.scanned = true
    } catch (error) {
      this.errorMessage = error.message
    } finally {
      this.loading = false
    }

    window.addUsageStat(USAGE_STAT_FEATURES.SmartFix, 'regex replace', 'scan')
  }

  async confirm() {
    if (this.loading || this.selectedTracks.length === 0) return

    this.loading = true

    const transformer: TRANSFORMERS = TRANSFORMERS.CustomFunction
    const settings: ITransformerSettings = { function: regexReplace, values: [this.field, this.regex, this.replaceString] }

    try {
      await this.$store.dispatch('transformer/execute', {
        tracks: this.selectedTracks,
        transformer,
        settings
      })

      this.$buefy.notification.open(`Tracks matching the regular expression have been updated`)

      window.addUsageStat(USAGE_STAT_FEATURES.SmartFix, 'regex replace', 'confirm')
    } catch (error) {
      console.error(error)

      if (this.$store.state.auth.isDemoUser) {
        this.$buefy.notification.open(`Tracks matching the regular expression have been updated`)
      }
    }

    this.loading = false
    this.scanned = false
    this.tracks = []
    this.selectedTracks = []
  }

  @Watch('selectedTemplate')
  templateWasSelected(v: IRegexTemplate) {
    if (!v) return

    this.regexString = v.regex
    this.replaceString = v.replace
    this.field = v.field

    this.flagInsensitive = v.flags.includes('i')
    this.flagGlobal = v.flags.includes('g')
  }

  @Watch('field', { immediate: true })
  updateColumnDefs() {
    this.columnDefs = [
      {
        field: this.field
      },
      {
        field: `New ${TrackFieldsLabels[this.field]}`,
        width: 150,
        valueGetter: (params: { data: Track }) => {
          return this.regexReplace(params.data)
        }
      }
    ]

    if (this.field !== TrackFields.Title) {
      this.columnDefs.unshift({
        field: TrackFields.Title
      })
    }
  }
}
